import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import styles from "./ConfirmDialog.module.css";

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Если задано — кнопка подтверждения активна только при точном совпадении введённого текста. */
  requireText?: string;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [typed, setTyped] = useState("");
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setTyped("");
      setPending({ ...options, resolve });
    });
  }, []);

  const close = useCallback((value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setPending(null);
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);
  const canConfirm = !pending?.requireText || typed === pending.requireText;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending && (
        <div className={styles.overlay} role="presentation" onClick={() => close(false)}>
          <div
            className={styles.dialog}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="confirm-title" className={styles.title}>
              {pending.title}
            </h2>
            {pending.description && <div className={styles.description}>{pending.description}</div>}
            {pending.requireText && (
              <label className={styles.field}>
                Введите «{pending.requireText}» для подтверждения
                <input
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  className={styles.input}
                />
              </label>
            )}
            <div className={styles.actions}>
              <button className={styles.cancel} onClick={() => close(false)}>
                {pending.cancelLabel ?? "Отмена"}
              </button>
              <button
                className={pending.danger ? styles.danger : styles.primary}
                disabled={!canConfirm}
                onClick={() => close(true)}
              >
                {pending.confirmLabel ?? "Подтвердить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- провайдер+хук в одном файле, устоявшийся паттерн
export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm: нет ConfirmProvider выше по дереву");
  return ctx.confirm;
}
